<template>
  <div class="solutionsearch_section">
    <div class="row controls">
      <div class="col-xl-6 col-md-8">
        <h3>{{ $t("solutions.search.title") }}</h3>
        <div class="search_field">
          <input
            id="searchTerm"
            v-model.trim="data.searchTerm"
            type="text"
            autocomplete="off"
            :placeholder="$t('solutions.search.placeholder')"
            @keydown="onSearchTermUpdated(data.searchTerm)"
            @keyup="onSearchTermUpdated(data.searchTerm)"
            @blur="onSearchTermUpdated(data.searchTerm)"
          />
        </div>
      </div>
      <div class="col-xl-6 col-md-4">
        <h3>{{ $t("solutions.search.categoryFilter.title") }}</h3>
        <select
          id="category"
          v-model="data.categoryId"
          class="form-control category-select"
          @change="onCategoryFilterUpdated(data.categoryId)"
        >
          @blur="updateCategoryFilter(categoryId)">
          <option key="all" :value="undefined">
            {{ $t("solutions.search.categoryFilter.all") }}
          </option>
          <option v-for="category in structure.categories" :key="category.id" :value="category.id">
            {{ category.name }}
          </option>
        </select>
      </div>
    </div>
    <div class="row">
      <div class="col-md-8">
        <div class="search_result_controls">
          <span v-if="isSearchRunning()" class="control_heading" data-cy="loader">{{
            $t("solutions.search.searching")
          }}</span>
          <span v-else>
            <span class="control_heading">{{ $t("solutions.search.searchResults") }} </span>
            <span v-if="searchResult.count > 0"
              >{{ searchResult.count }} {{ $t("solutions.search.documents") }}</span
            >
            <span v-else>{{ $t("solutions.search.noResults") }}</span>
          </span>
        </div>
        <div :class="getResultListClasses()" class="result-list">
          <div
            v-for="article in searchResult.value"
            :key="article.id"
            class="row article"
            @click.prevent.stop="
              $router.push({
                path: `/support/solutions/categories/${article.categoryId}/${article.folderId}/${article.freshdeskId}`,
              })
            "
          >
            <div class="col-md-12 title">
              <span :class="getType(article)" class="icon"></span>{{ article.title }}
            </div>
            <div class="col-md-12 excerpt">
              {{ getExcerpt(article) }}
            </div>
            <div class="col-md-12 hierarchy">
              <span class="root">{{ $t("solutions.article.breadcrumb.root") }}</span> /
              <span class="category">{{ getCategoryName(article) }}</span> /
              <span class="folder">{{ getFolderName(article) }}</span>
            </div>
          </div>
        </div>
        <div class="loadMore">
          <span v-if="loadingMore">{{ $t("solutions.search.loadingMore") }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ISolutionsState } from "@/store/solutionsModule";
import { getCategoryName, getFolderName, isVideo } from "@/util/SolutionUtil";
import { ISolutionArticle } from "@iloq/shared";
import _ from "lodash";
import { defineComponent } from "vue";
import { mapState } from "vuex";

export default defineComponent({
  name: "SolutionSearch",
  data(): any {
    return {
      data: {
        data: {
          searchTerm: "",
          lastSearchTerm: "x",
        },
        categoryId: undefined as number | undefined,
        lastCategoryId: undefined as number | undefined,
      },
      typing: false,
      searchResults: {
        count: 0,
        facets: {},
        value: [] as ISolutionArticle[],
      },
    };
  },
  computed: {
    ...mapState("solutions", {
      searchQuery: (state: unknown) => (state as ISolutionsState).search.searchQuery,
      searchResult: (state: unknown) => (state as ISolutionsState).search.searchResult,
      searching: (state: unknown) => (state as ISolutionsState).search.searching,
      loadingMore: (state: unknown) => (state as ISolutionsState).search.loadingMore,
      structure: (state: unknown) => (state as ISolutionsState).structure,
    }),
  },
  watch: {
    "$route.query.term": "onTermParamChanged",
    "$route.query.category": "onCategoryParamChanged",
  },
  methods: {
    getType(article: ISolutionArticle) {
      return isVideo(article) ? "video" : "article";
    },
    initParams() {
      const passedTerm: string = (this.$route.query as any).term || "";
      const passedCategoryId: string | undefined = (this.$route.query as any).category;
      const parsedCategoryId = passedCategoryId ? parseInt(passedCategoryId, 10) : undefined;

      this.data.searchTerm = passedTerm;
      this.data.categoryId = parsedCategoryId;

      this.onSearchParametersUpdated(passedTerm, parsedCategoryId);
    },
    onTermParamChanged() {
      const passedTerm: string | undefined = (this.$route.query as any).term;
      if (passedTerm) {
        this.data.searchTerm = passedTerm;
        this.onSearchTermUpdated(passedTerm);
      }
    },
    onCategoryParamChanged() {
      const passedCategoryId: string | undefined = (this.$route.query as any).category;
      const parsedCategoryId = passedCategoryId ? parseInt(passedCategoryId, 10) : undefined;
      if (passedCategoryId && parsedCategoryId) {
        this.data.categoryId = parsedCategoryId;
        this.onCategoryFilterUpdated(parsedCategoryId);
      } else {
        this.data.categoryId = undefined;
        this.onCategoryFilterUpdated(undefined);
      }
    },
    onSearchTermUpdated(term: string) {
      this.onSearchParametersUpdated(term, this.data.categoryId);
    },
    onCategoryFilterUpdated(categoryId?: number) {
      this.onSearchParametersUpdated(this.data.searchTerm || "", categoryId);
    },
    onSearchParametersUpdated(term: string, categoryId?: number) {
      const termUpdated = term.trim() !== (this.data.lastSearchTerm || "").trim();
      const categoryIdUpdated = categoryId !== this.data.lastCategoryId;
      if (termUpdated || categoryIdUpdated) {
        this.typing = true;

        // The following prevents wrong/old results from being displayed
        if (termUpdated) {
          this.$store.dispatch("solutions/updateTermWithoutSearching", term);
        }
        if (categoryIdUpdated) {
          this.$store.dispatch("solutions/updateCategoryIdWithoutSearching", categoryId);
        }

        this.search(term, categoryId);
      }
    },
    search: _.debounce(function (this: any, term: string, categoryId?: number) {
      if (term !== this.data.lastSearchTerm || categoryId !== this.data.lastCategoryId) {
        this.data.lastSearchTerm = term;
        this.data.lastCategoryId = categoryId;
        this.$store.dispatch("solutions/updateQueryPartially", { term, categoryId });
        this.typing = false;
      }
    }, 400),
    isSearchRunning() {
      return this.typing || this.searching;
    },
    getResultListClasses() {
      if (this.isSearchRunning()) {
        return "stale";
      }
      return "up-to-date";
    },
    getCategoryName(article: ISolutionArticle) {
      return getCategoryName(this.structure, article.categoryId, article.categoryName);
    },
    getFolderName(article: ISolutionArticle) {
      return getFolderName(this.structure, article.folderId, article.folderName);
    },
    getExcerpt(article: ISolutionArticle) {
      const excerptLength = 220;
      if (article.descriptionText.length <= excerptLength) {
        return article.descriptionText;
      }
      const excerpt = article.descriptionText.substring(0, excerptLength);
      return `${excerpt.substring(0, (excerpt.lastIndexOf(" ") || excerptLength - 1) + 1)}...`;
    },
  },
  mounted() {
    this.data.searchTerm = this.searchQuery.term;
    this.data.lastSearchTerm = this.searchQuery.term;
    this.data.categoryId = this.searchQuery.categoryId;
    this.data.lastCategoryId = this.searchQuery.categoryId;
    window.scrollTo(0, 0);

    window.onscroll = () => {
      if (this.searchResult.value.length < this.searchResult.count && !this.loadingMore) {
        const loadMoreDiv = document.querySelector(".loadMore");
        const bounding = loadMoreDiv!.getBoundingClientRect();
        if (
          bounding.top >= 0 &&
          bounding.left >= 0 &&
          bounding.right <= (window.innerWidth || document.documentElement.clientWidth) &&
          bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight)
        ) {
          this.$store.dispatch("solutions/loadMoreArticles");
        }
      }
    };

    this.initParams();
  },
  beforeDestroy() {
    window.onscroll = () => {};
  },
});
</script>

<style lang="scss">
@import "../design-system/assets/tokens/tokens";
$bootsrap-default-spacing: 15px;

.solutionsearch_section {
  .controls {
    margin-bottom: 40px;
    h3 {
      font-size: 16px;
      font-weight: 700;
      margin-top: 30px;
      margin-bottom: 12px;
    }
    .search_field input {
      width: 100%;
      height: 50px;
      background: url("/img/components/icons/search.svg") no-repeat scroll 15px center;
      background-size: 24px 24px;
      padding-left: 55px;
      padding-top: 8px;
      font-size: 16px;
      font-weight: 400;
      color: $color-iloq-gray-dark;
      border: 1px solid $color-iloq-gray-light;
      border-radius: 7px;
      box-shadow: 0 1px 4px 0 darken($color-iloq-gray-light, 20%);

      margin-top: 0px;
      &:focus {
        outline: none;
      }

      ::placeholder {
        /* Chrome, Firefox, Opera, Safari 10.1+ */
        color: $color-iloq-gray;
        opacity: 1; /* Firefox */
        font-style: italic;
      }
      &::-webkit-input-placeholder {
        color: $color-iloq-gray;
        font-style: italic;
      }
      &:-moz-placeholder {
        color: $color-iloq-gray;
        font-style: italic;
      }
      &::-moz-placeholder {
        color: $color-iloq-gray;
        font-style: italic;
      }
      &:-ms-input-placeholder {
        /* Internet Explorer 10-11 */
        color: $color-iloq-gray;
        font-style: italic;
      }
      &::-ms-input-placeholder {
        /* Microsoft Edge */
        color: $color-iloq-gray;
        font-style: italic;
      }
    }

    // copypasta starts (from RMARequestEditor.vue)
    .form-control {
      font-size: $size-ms;
      color: $color-iloq-gray-dark;
      padding: 0.375rem 0.6rem 0.155rem !important;
    }
    select {
      background: url("data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz\
    48c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1\
    sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAg\
    NC45IDEwIiBzdHlsZT0iZW5hYmxlLWJhY2tncm91bmQ6bmV3IDAgMCA0LjkgMTA7IiB4bWw6c3BhY2U9InByZXNlcnZlI\
    j48c3R5bGUgdHlwZT0idGV4dC9jc3MiPi5zdDB7ZmlsbDpub25lO30uc3Qxe2ZpbGw6bm9uZTtzdHJva2U6IzAwNDM1Qj\
    tzdHJva2Utd2lkdGg6MC41O3N0cm9rZS1saW5lY2FwOnJvdW5kO3N0cm9rZS1saW5lam9pbjpyb3VuZDtzdHJva2UtbWl\
    0ZXJsaW1pdDoxMDt9PC9zdHlsZT48dGl0bGU+YXJyb3dzPC90aXRsZT48cmVjdCB5PSIwIiBjbGFzcz0ic3QwIiB3aWR0\
    aD0iNC45IiBoZWlnaHQ9IjEwIi8+PHBvbHlsaW5lIGNsYXNzPSJzdDEiIHBvaW50cz0iMy43LDQuMSAyLjUsNS42IDEuM\
    yw0LjEgIi8+PC9zdmc+")
        no-repeat 98% 50% !important;
      -moz-appearance: none;
      -webkit-appearance: none;
      font-size: 14px !important;
      appearance: none;
      box-shadow: none;
      border-radius: 3px;
      border: 1px solid #ddd;
      &:focus {
        border: 1px solid #0a485f;
        box-shadow: none;
      }
      &:disabled {
        background-color: #f3f3f3;
      }
    }
    // copypasta ends

    select {
      background-position-x: 96% !important;
    }
    .form-control {
      padding-left: 15px !important;
      height: 40px;
    }
    .category-select {
      max-width: 250px;
    }
  }

  .search_result_controls {
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    color: $color-iloq-gray-dark;
    .control_heading {
      color: $color-iloq-blue-dark;
    }
  }

  .result-list {
    font-size: 14px;
    margin-left: $bootsrap-default-spacing;
    margin-right: $bootsrap-default-spacing;

    &.stale {
      opacity: 0.2;
    }

    .article {
      cursor: pointer;
      padding-top: 16px;
      padding-bottom: 12px;
      overflow: auto;
      line-height: 1.1;
      //border: 2px solid white;
      border-top-width: 1px;
      border-bottom: 1px solid $color-iloq-gray-light;
      &:last-child {
        border-bottom: 1px solid $color-white;
      }

      .icon {
        display: inline-block;
        background: url("/img/components/icons/article.svg") no-repeat scroll center center;
        background-size: 16px 16px;
        margin: 0 10px -4px 0;
        padding: 0;
        width: 16px;
        height: 16px;
        border: none;
        &.video {
          background: url("/img/components/icons/video.svg") no-repeat scroll center center;
        }
      }
      .title {
        color: $color-iloq-blue-dark;
        font-size: 16px;
        line-height: 22px;
        font-weight: bold;
      }
      .excerpt {
        color: $color-iloq-gray-dark;
        font-size: 14px;
        font-weight: $weight-book;
        line-height: 20px;
        padding-top: 10px;
        padding-bottom: 5px;
      }
      .hierarchy {
        font-size: 9px;
        line-height: 20px;
        color: $color-iloq-gray;
        font-weight: $weight-bold;
        margin: 5px 0 0;
        span {
          text-transform: uppercase;
          color: $color-iloq-black;
          font-weight: $weight-normal;
          background: $color-iloq-gray-lightest;
          margin: 0px 5px;
          padding: 5px 12px 3px 12px;
          border-radius: 4px;
          white-space: nowrap;
        }
      }

      &:hover {
        background: $color-iloq-gray-light;
      }
      &.selected {
        background: $color-iloq-gray-light;
        border-color: $color-iloq-blue-light;
      }
    }
  }

  .loadMore {
    font-size: 14px;
    color: $color-iloq-gray;
    text-align: center;
  }
}
</style>
